@if (resolving()) {
  <div class="grid min-h-dvh place-items-center">
    <span class="loading loading-bars loading-lg text-neutral"></span>
  </div>
} @else if (!open()) {
  <app-dev-gate (opened)="open.set(true)" />
} @else {
  <!--
    `dvh`, not `vh`: on mobile Safari `100vh` measures the screen as if the
    browser bars were hidden, which pushed the footer below the fold.
  -->
  <div class="flex min-h-dvh flex-col bg-base-200">
    <header
      class="navbar sticky top-0 z-50 gap-1 border-b-[3px] border-neutral px-4 max-[389px]:gap-0.5 max-[389px]:px-1"
      [class.bg-base-100]="!isDev"
      [class.bg-info]="isDev"
      [class.text-info-content]="isDev"
    >
      <a
        routerLink="/home"
        class="mr-2 font-mono text-xl font-bold tracking-tighter uppercase max-[389px]:mr-1 max-[389px]:text-lg"
      >
        corti<span class="bg-primary px-1 text-primary-content">.to</span>
      </a>

      @if (isDev) {
        <span
          data-dev
          class="mr-1 hidden border-[3px] border-neutral bg-neutral px-2 py-0.5 font-mono text-xs font-bold text-neutral-content uppercase sm:inline-block"
          [title]="t().nav.devHint"
        >
          {{ t().nav.dev }}
        </span>
      }

      <nav class="flex items-center gap-1">
        <a
          routerLink="/home"
          routerLinkActive="bg-primary text-primary-content"
          class="btn btn-square btn-ghost border-[3px] border-transparent hover:border-neutral"
          [attr.aria-label]="t().nav.home"
        >
          <span class="material-symbols-outlined">home</span>
        </a>
        @if (auth.signedIn()) {
          <a
            routerLink="/list"
            routerLinkActive="bg-primary text-primary-content"
            class="btn btn-square btn-ghost border-[3px] border-transparent hover:border-neutral"
            [attr.aria-label]="t().nav.list"
          >
            <span class="material-symbols-outlined">format_list_bulleted</span>
          </a>
        }
      </nav>

      <div class="ml-auto flex items-center gap-2 max-[389px]:gap-1">
        <button
          type="button"
          class="btn btn-square border-[3px] border-neutral bg-base-100 font-mono font-bold brutal-sm brutal-press"
          [attr.aria-label]="t().language.label(languageButton().name)"
          [title]="t().language.label(languageButton().name)"
          (click)="i18n.next()"
        >
          {{ languageButton().code }}
        </button>
        <button
          type="button"
          class="btn btn-square border-[3px] border-neutral bg-base-100 brutal-sm brutal-press"
          [attr.aria-label]="t().theme.label(themeButton().label)"
          [title]="t().theme.label(themeButton().label)"
          (click)="theme.next()"
        >
          <span class="material-symbols-outlined">{{ themeButton().icon }}</span>
        </button>
        @if (auth.loading()) {
          <span class="loading loading-bars loading-sm text-neutral"></span>
        } @else {
          @if (auth.user(); as user) {
            <div class="dropdown dropdown-end">
              <div
                tabindex="0"
                role="button"
                class="btn btn-square border-[3px] border-neutral bg-base-100 p-0 brutal-sm brutal-press"
                [attr.aria-label]="t().nav.account"
              >
                @if (user.photoURL) {
                  <img class="size-full object-cover" [src]="user.photoURL" alt="" />
                } @else {
                  <span class="material-symbols-outlined">account_circle</span>
                }
              </div>
              <ul
                tabindex="0"
                class="menu dropdown-content z-50 mt-2 max-h-[70dvh] w-56 flex-nowrap overflow-y-auto border-[3px] border-neutral bg-base-100 p-2 brutal"
              >
                <li class="menu-title truncate font-mono text-xs">
                  {{ user.displayName || user.email || t().nav.signedIn }}
                </li>
                <li>
                  <button type="button" data-open-api (click)="apiDialog()?.open()">
                    <span class="material-symbols-outlined">terminal</span> {{ t().nav.api }}
                  </button>
                </li>
                <li>
                  <button type="button" (click)="auth.signOut()">
                    <span class="material-symbols-outlined">logout</span> {{ t().nav.signOut }}
                  </button>
                </li>
              </ul>
            </div>
          } @else {
            <div class="dropdown dropdown-end">
              <div
                tabindex="0"
                role="button"
                class="btn btn-primary border-[3px] border-neutral font-bold uppercase brutal-sm brutal-press max-[389px]:px-2"
              >
                @if (auth.signingIn()) {
                  <span class="loading loading-bars loading-sm"></span>
                } @else {
                  {{ t().nav.signIn }}
                }
              </div>
              <ul
                tabindex="0"
                class="menu dropdown-content z-50 mt-2 max-h-[70dvh] w-56 flex-nowrap overflow-y-auto border-[3px] border-neutral bg-base-100 p-2 brutal"
              >
                <li>
                  <button type="button" (click)="auth.signInWithGoogle()">
                    {{ t().nav.withGoogle }}
                  </button>
                </li>
                <li>
                  <button type="button" (click)="auth.signInWithFacebook()">
                    {{ t().nav.withFacebook }}
                  </button>
                </li>
                <li>
                  <button type="button" (click)="auth.signInWithTwitter()">
                    {{ t().nav.withX }}
                  </button>
                </li>
              </ul>
            </div>
          }
        }
      </div>
    </header>

    <main class="flex-1">
      <router-outlet />
    </main>

    <footer
      class="sticky bottom-0 z-40 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 border-t-[3px] border-neutral px-4 py-3 font-mono text-xs uppercase"
      [class.bg-base-100]="!isDev"
      [class.bg-info]="isDev"
      [class.text-info-content]="isDev"
    >
      <span>v{{ appVersion }}</span>
      @if (isDev) {
        <span data-dev-note class="font-bold" [title]="t().nav.devHint">
          <span class="sm:hidden">{{ t().nav.dev }}</span>
          <span class="hidden sm:inline">{{ t().nav.devNote }}</span>
        </span>
      }
      <a
        class="py-1 hover:bg-primary hover:text-primary-content"
        href="https://github.com/cristianmiranda"
        target="_blank"
        rel="noopener"
      >
        @cristianmiranda
      </a>
      <a
        class="flex items-center gap-1 py-1 hover:bg-primary hover:text-primary-content"
        href="https://cafecito.app/crismiranda"
        target="_blank"
        rel="noopener"
        [attr.aria-label]="t().nav.coffee"
        [title]="t().nav.coffee"
      >
        <span class="material-symbols-outlined text-base">local_cafe</span>
        <span class="hidden sm:inline">{{ t().nav.coffee }}</span>
      </a>
    </footer>

    @if (auth.signedIn()) {
      <app-api-dialog />
    }

    @if (auth.blocked()) {
      <div class="toast toast-center toast-bottom z-50">
        <div
          class="alert items-center border-[3px] border-neutral bg-error text-error-content brutal"
        >
          <span class="text-sm font-bold">{{ t().nav.signInBlocked }}</span>
          <button
            type="button"
            class="btn btn-sm border-[3px] border-neutral bg-base-100 font-bold uppercase brutal-sm brutal-press"
            (click)="auth.blocked.set(false)"
          >
            {{ t().nav.close }}
          </button>
        </div>
      </div>
    }
  </div>
}
